import { createApi } from '@redux/createApi';
import { createSlice } from '@reduxjs/toolkit';
import { serialize } from '../../../shared/lib/serialize';
import { format } from '../../../shared/lib/format';

const tags = {
  getTransactions: 'getTransactions',
  getTransaction: 'getTransaction',
  getKsbTransactions: 'getKsbTransactions',
};

export const transactionsApi = createApi({
  reducerPath: 'transactionsApi',
  tagTypes: [tags.getTransactions, tags.getTransaction, tags.getKsbTransactions],
  endpoints: (builder) => ({
    getTransaction: builder.mutation({
      query: ({ pk, sk }) => {
        return ({
          method: 'post', url: `/transaction-info/transaction`,
          body: { pk: pk, sk: sk },
        });
      },
      providesTags: [tags.getTransaction],
    }),
    getTransactions: builder.query({
      query: ({ limit, start, end, direction, tx_type, asset, receiver, name, transaction_id, from_amount, to_amount, tx_status, endOfDay, cursor }) => {
        let params = {
          page_size: limit,
        };

        if (start) {
          const formatted = `${start.getDate()}/${format.addZeroBefore(start.getMonth() + 1)}/${start.getFullYear()}`;
          if (endOfDay) {
            params.start_end_of_day = formatted;
          } else {
            params.start = formatted;
          }
        }

        if (end) {
          const formatted = `${end.getDate()}/${format.addZeroBefore(end.getMonth() + 1)}/${end.getFullYear()}`;
          if (endOfDay) {
            params.end_end_of_day = formatted;
          } else {
            params.end = formatted;
          }
        }

        if (direction) {
          params.direction = direction;
        }

        if (tx_type) {
          params.tx_type = tx_type;
        }

        if (asset) {
          params.asset = asset;
        }

        if (receiver) {
          //ошибка на бекенде вместо network должно быть receiver  на самом деле
          params.network = receiver;
        }

        if (name) {
          params.name = name;
        }

        if (transaction_id) {
          params.transaction_id = transaction_id;
        }

        if (from_amount) {
          params.from_amount = format.parseAmount(from_amount);
        }

        if (to_amount) {
          params.to_amount = format.parseAmount(to_amount);
        }

        if (tx_status) {
          params.tx_status = tx_status;
        }

        return ({
          method: 'post',
          url: `/transaction-info/transaction/list/v2?${serialize(params)}`,
          body: cursor || undefined,
        });
      },
      providesTags: [tags.getTransactions],
      serializeQueryArgs: ({ queryArgs }) => {
        return { ...queryArgs };
      },
      transformResponse: (data, meta) => {
        if (meta?.response?.status === 422 && data?.message === '') {
          return {
            list: [],
            nextCursor: null,
          };
        }
        return {
          list: data.transaction_list || [],
          nextCursor: data.next_cursor || null,
        };
      },
    }),
    getKsbTransactions: builder.query({
      query: (queryParams) => {
        const [page, limit, start, end, tx_status, service] = queryParams;

        let params = {
          page_number: page,
          page_size: limit,
        };

        if (start) {
          params = {
            ...params,
            start: `${start.getDate()}/${format.addZeroBefore(start.getMonth() + 1)}/${start.getFullYear()}`,
          };
        }

        if (end) {
          params = {
            ...params,
            end: `${end.getDate()}/${format.addZeroBefore(end.getMonth() + 1)}/${end.getFullYear()}`,
          };
        }

        if (tx_status) {
          params = {
            ...params,
            tx_status,
          };
        }

        if (service) {
          params = {
            ...params,
            service,
          };
        }

        return ({
          method: 'get',
          url: `/transaction-info/ksb/transaction/list?${serialize(params)}`,
        });
      },
      providesTags: [tags.getKsbTransactions],
      transformResponse: (data) => {
        return {
          list: data.transaction_list,
          count: data.page_amount,
        };
      },
    }),
  }),
});

export const transactionsParams = createSlice({
  name: 'transactionsParams',
  initialState: {
    limit: 50,
    filter: 'ALL',
    start: null,
    end: null,
    direction: null,
    tx_type: null,
    network: null,
    asset: null,
    receiver: null,
    name: '',
    transaction_id: '',
    from_amount: '',
    to_amount: '',
    tx_status: '',
    cursors: [null],
    currentPageIndex: 0,
  },
  reducers: {
    setParams: (state, action) => {
      return { ...state, ...action.payload, cursors: [null], currentPageIndex: 0 };
    },
    goToNextPage: (state, action) => {
      const nextCursor = action.payload;
      if (state.currentPageIndex + 1 >= state.cursors.length) {
        state.cursors.push(nextCursor);
      }
      state.currentPageIndex += 1;
    },
    goToPrevPage: (state) => {
      if (state.currentPageIndex > 0) {
        state.currentPageIndex -= 1;
      }
    },
  },
});

export const ksbTransactionsParams = createSlice({
  name: 'ksbTransactionsParams',
  initialState: {
    page: 1,
    limit: 25,
    start: null,
    end: null,
    tx_status: '',
    service: '',
  },
  reducers: {
    setKsbParams: (state, action) => {
      return { ...state, ...action.payload };
    },
  },
});

export const {
  useGetTransactionsQuery,
  useGetTransactionMutation,
  useGetKsbTransactionsQuery,
} = transactionsApi;

export const { setParams, goToNextPage, goToPrevPage } = transactionsParams.actions;
export const { setKsbParams } = ksbTransactionsParams.actions;
export const transactionsParamsReducer = transactionsParams.reducer;
export const ksbTransactionsParamsReducer = ksbTransactionsParams.reducer;
export const transactionsParamsSelector = (state) => state.transactionsParams;
export const ksbTransactionsParamsSelector = (state) => state.ksbTransactionsParams;